import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import type { MemberIdentifierType } from "domains/inventory/services/types";
import { InventoryFlowContext } from "contexts/InventoryFlowContext";

type EquipmentRentalState = {
  contact: string | null;
  memberId: string | null;
  identifierType: MemberIdentifierType | null;
  identifier: string | null;
};

type EquipmentRentalContextValue = EquipmentRentalState & {
  setContext: (next: Partial<EquipmentRentalState>) => void;
  reset: () => void;
};

const defaultState: EquipmentRentalState = {
  contact: null,
  memberId: null,
  identifierType: null,
  identifier: null,
};

const EquipmentRentalContext = createContext<EquipmentRentalContextValue | undefined>(undefined);

export function EquipmentRentalProvider({ children }: { children: ReactNode }) {
  const flowContext = useContext(InventoryFlowContext);
  const [state, setState] = useState<EquipmentRentalState>(defaultState);

  const setContext = useCallback(
    (next: Partial<EquipmentRentalState>) => {
      if (flowContext) {
        const current = flowContext.state;
        flowContext.setIdentity({
          contact: next.contact ?? current.contact ?? null,
          memberId: next.memberId ?? current.memberId ?? null,
          identifierType: next.identifierType ?? current.identifierType ?? null,
          identifier: next.identifier ?? current.identifier ?? null,
        });
        return;
      }
      setState((current) => ({ ...current, ...next }));
    },
    [flowContext]
  );

  const flowClear = flowContext?.clearFlow;
  const reset = useCallback(() => {
    if (flowClear) {
      flowClear();
      return;
    }
    setState(defaultState);
  }, [flowClear]);

  const effectiveState = useMemo(() => {
    if (flowContext) {
      return {
        contact: flowContext.state.contact ?? null,
        memberId: flowContext.state.memberId ?? null,
        identifierType: flowContext.state.identifierType ?? null,
        identifier: flowContext.state.identifier ?? null,
      };
    }
    return state;
  }, [flowContext, state]);

  const value = useMemo(
    () => ({
      ...effectiveState,
      setContext,
      reset,
    }),
    [effectiveState, reset, setContext]
  );

  return (
    <EquipmentRentalContext.Provider value={value}>{children}</EquipmentRentalContext.Provider>
  );
}

export function useEquipmentRental() {
  const context = useContext(EquipmentRentalContext);
  if (!context) {
    throw new Error("useEquipmentRental must be used within EquipmentRentalProvider");
  }
  return context;
}
